import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { EmptyChartState } from "@/components/common/EmptyChartState";
import type { AccommodationFundingTrendPoint } from "@/types";
import { Divider } from "@mui/material";
import { currencyCompactNotation, formatUSD } from "@/utils/formatters/currencyFormatter";
import { useAppTheme } from "@/hooks/useAppTheme";
import { getChartTooltipStyle, getCartesianGridProps } from "../utils/chartStyles";

interface AccommodationFundingTrendChartProps {
  data: AccommodationFundingTrendPoint[];
}

export const AccommodationFundingTrendChart = ({ data }: AccommodationFundingTrendChartProps) => {
  const { theme } = useAppTheme();

  if (data.length === 0) {
    return <EmptyChartState />;
  }

  const chartData = data.map((d) => ({
    month: d.month,
    funded: d.total_accommodation_funded,
  }));

  return (
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={chartData} margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
        <CartesianGrid {...getCartesianGridProps(theme)} />
        <XAxis dataKey="month" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
        <YAxis
          tickFormatter={(value) => formatUSD(value, currencyCompactNotation)}
          tick={{ fontSize: 10 }}
          tickLine={false}
          axisLine={false}
          width={60}
        />
        <Tooltip
          content={({ payload, label }) => {
            if (!payload || !payload.length) return null;
            return (
              <div style={{ ...getChartTooltipStyle(theme), padding: "8px" }}>
                <strong>{label}</strong>
                <Divider sx={{ mb: 1, mt: 1 }} />
                <div>Funded: {formatUSD(payload[0].value as number)}</div>
              </div>
            );
          }}
        />
        <Line
          type="monotone"
          dataKey="funded"
          stroke={theme.palette.primary.main}
          strokeWidth={2}
          dot={{ r: 3, fill: theme.palette.primary.main }}
          activeDot={{ r: 5 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};
